/**
 * Formats the market-overview reader's `generated_at` timestamp for the "AI
 * Market Summary" card (mobile Brief tab and the desktop Market Analysis
 * header), in America/New_York — the summary is generated on the NY
 * trading-session schedule (see workers/shared/marketOverviewSchedule.js).
 */

const ET_TIME = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true,
});
const ET_WEEKDAY = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', weekday: 'short',
});
const ET_MONTH_DAY = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', month: 'short', day: 'numeric',
});
const ET_YMD_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
});

// The ET calendar date of `date`, as a plain {y, m, d} — not an instant, so it
// can be diffed as pure calendar days without any real-elapsed-time math
// (which a DST transition would throw off by an hour).
function etYMD(date) {
  const p = Object.fromEntries(ET_YMD_PARTS.formatToParts(date).map((x) => [x.type, x.value]));
  return { y: +p.year, m: +p.month, d: +p.day };
}

const calendarDaysBetween = (a, b) =>
  Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86400000);

/**
 * "4:15 PM ET · Today" / "9:45 AM ET · Yesterday" / "1:00 PM ET · Wed" /
 * "4:15 PM ET · Sep 12" (7+ days old, or clock skew put it in the future).
 * Returns null for a missing/unparseable timestamp so callers render nothing
 * rather than a broken label.
 */
export function formatMarketOverviewUpdated(generatedAtIso, now = new Date()) {
  if (!generatedAtIso) return null;
  const generated = new Date(generatedAtIso);
  if (Number.isNaN(generated.getTime())) return null;

  const time = `${ET_TIME.format(generated)} ET`;
  const dayDiff = calendarDaysBetween(etYMD(generated), etYMD(now));

  let dayLabel;
  if (dayDiff <= 0) dayLabel = 'Today'; // 0, or a future timestamp from clock skew
  else if (dayDiff === 1) dayLabel = 'Yesterday';
  else if (dayDiff < 7) dayLabel = ET_WEEKDAY.format(generated);
  else dayLabel = ET_MONTH_DAY.format(generated);

  return `${time} · ${dayLabel}`;
}
